import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGIN_PATH } from "@/const";
import { trpc } from "@/lib/trpc";
import { Award, CheckCircle2, Loader2, XCircle, Download } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link, useRoute } from "wouter";

export default function Result() {
  const { user, loading: authLoading } = useAuth();
  const [, params] = useRoute("/result/:attemptId");
  const attemptId = params?.attemptId ? Number(params.attemptId) : null;
  const [passedForm, setPassedForm] = useState({
    name: "",
    birthDate: "",
    gender: "male",
    prefecture: "",
    email: "",
  });

  const prefectures = [
    "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県",
    "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県",
    "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県",
    "岐阜県", "静岡県", "愛知県", "三重県",
    "滋賀県", "京都府", "大阪府", "兵庫県", "奈良県", "和歌山県",
    "鳥取県", "島根県", "岡山県", "広島県", "山口県",
    "徳島県", "香川県", "愛媛県", "高知県",
    "福岡県", "佐賀県", "長崎県", "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県",
  ];

  const { data: attemptData, isLoading: attemptLoading } = trpc.examAttempts.get.useQuery(
    { id: attemptId! },
    { enabled: !!attemptId && !!user }
  );

  const attempt = attemptData?.attempt;
  const answers = attemptData?.answers || [];

  const { data: session } = trpc.examSessions.byId.useQuery(
    { id: attempt?.sessionId! },
    { enabled: !!attempt?.sessionId }
  );

  const { data: level } = trpc.examLevels.byId.useQuery(
    { id: session?.levelId! },
    { enabled: !!session?.levelId }
  );

  const { data: badge } = trpc.badges.myBadges.useQuery(
    undefined,
    { enabled: !!user && !!attempt?.passed }
  );

  const sendPassedInfoEmail = trpc.notifications.sendPassedInfoEmail.useMutation({
    onSuccess: () => {
      toast.success("確認メールを送信しました");
    },
    onError: (error) => {
      // Zod validation errors from tRPC come as JSON array strings
      let message = "確認メールの送信に失敗しました";
      try {
        const parsed = JSON.parse(error.message);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].message) {
          message = parsed[0].message;
        }
      } catch {
        if (error.message) {
          message = error.message;
        }
      }
      toast.error(message);
    },
  });

  useEffect(() => {
    if (!user) return;
    setPassedForm((prev) => ({
      ...prev,
      name: prev.name || user.name || "",
      email: prev.email || user.email || "",
    }));
  }, [user]);

  if (authLoading || attemptLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>ログインが必要です</CardTitle>
            <CardDescription>
              試験結果を表示するには、ログインしてください。
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link href={LOGIN_PATH}>
              <Button className="w-full">ログイン</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!attempt || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>結果が見つかりません</CardTitle>
            <CardDescription>
              試験結果が見つかりませんでした。
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link href="/my-page">
              <Button className="w-full">マイページに戻る</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const displayName = session.displayName || level?.displayName || session.name;
  const userBadge = badge?.find((b) => b.levelId === session.levelId);
  const totalQuestionCount = session.questionCount || answers.length || 0;
  const correctAnswerCount = answers.filter((a) => a.isCorrect).length || 0;

  const badgeImageUrl = session.badgeImageUrl || level?.badgeImageUrl;
  const isBadgeExpired = userBadge?.expiresAt ? new Date(userBadge.expiresAt) < new Date() : false;

  const handleBadgeDownload = async () => {
    if (!badgeImageUrl) {
      toast.error("ダウンロード可能なバッジ画像がありません");
      return;
    }

    try {
      const response = await fetch(`/api/badge-image/${session.id}?source=session`, { cache: 'no-store' });
      if (!response.ok) {
        let message = "バッジ画像の取得に失敗しました";
        try {
          const errorPayload = await response.json();
          if (errorPayload?.error && typeof errorPayload.error === "string") {
            message = errorPayload.error;
          }
        } catch {
          // Keep default message when response body is not JSON.
        }
        throw new Error(message);
      }

      const rawContentType = response.headers.get("content-type") || "";
      const contentType = rawContentType.split(";")[0].trim();
      if (!contentType.startsWith("image/")) {
        throw new Error("画像データではありません");
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("バッジ画像の読み込みに失敗しました"));
        img.src = objectUrl;
      });

      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("キャンバスの初期化に失敗しました");
      }

      context.drawImage(image, 0, 0);

      const badgeName = (passedForm.name || user.name || "").trim();
      if (badgeName) {
        const fontSize = Math.max(24, Math.round(canvas.width * 0.035));
        context.font = `700 ${fontSize}px sans-serif`;
        context.textAlign = "center";
        context.textBaseline = "middle";

        const textY = Math.round(canvas.height * 0.88);
        const stripHeight = Math.max(42, Math.round(fontSize * 1.9));

        context.fillStyle = "rgba(0, 0, 0, 0.45)";
        context.fillRect(0, textY - Math.round(stripHeight / 2), canvas.width, stripHeight);

        context.fillStyle = "#ffffff";
        context.fillText(badgeName, Math.round(canvas.width / 2), textY);
      }

      const outputType = "image/png";
      const personalizedBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((result) => {
          if (result) resolve(result);
          else reject(new Error("バッジ画像の書き出しに失敗しました"));
        }, outputType);
      });

      const personalizedUrl = URL.createObjectURL(personalizedBlob);
      const link = document.createElement("a");
      link.href = personalizedUrl;

      link.download = `${displayName}_badge.png`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(personalizedUrl);
      URL.revokeObjectURL(objectUrl);
      toast.success("名前入りバッジ画像をダウンロードしました");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "バッジ画像のダウンロードに失敗しました");
    }
  };

  const handleOpenConfirmMail = () => {
    const required = [passedForm.name, passedForm.birthDate, passedForm.gender, passedForm.prefecture, passedForm.email]
      .every((value) => String(value || "").trim().length > 0);

    if (!required) {
      toast.error("必須項目をすべて入力してください");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(passedForm.email.trim())) {
      toast.error("有効なメールアドレスを入力してください");
      return;
    }

    sendPassedInfoEmail.mutate({
      to: passedForm.email,
      name: passedForm.name,
      birthDate: passedForm.birthDate,
      gender: passedForm.gender as "male" | "female" | "unknown",
      prefecture: passedForm.prefecture,
      examName: displayName,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-purple-100 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link href="/">
            <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent cursor-pointer">
              Biz-AI
            </span>
          </Link>
          <h1 className="text-xl font-bold text-gray-800">試験結果</h1>
          <Link href="/my-page">
            <Button variant="outline" size="sm">マイページ</Button>
          </Link>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto space-y-6">
          <Card className={attempt.passed ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50"}>
            <CardContent className="pt-6">
              <div className="text-center">
                {attempt.passed ? (
                  <>
                    <CheckCircle2 className="w-20 h-20 text-green-600 mx-auto mb-4" />
                    <h2 className="text-3xl font-bold text-green-700 mb-2">合格おめでとうございます！</h2>
                    <p className="text-green-600">
                      {displayName}に合格しました。
                    </p>
                  </>
                ) : (
                  <>
                    <XCircle className="w-20 h-20 text-red-600 mx-auto mb-4" />
                    <h2 className="text-3xl font-bold text-red-700 mb-2">不合格</h2>
                    <p className="text-red-600">
                      残念ながら、今回は不合格でした。再度チャレンジしてください。
                    </p>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>試験結果詳細</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">スコア</div>
                  <div className="text-3xl font-bold text-indigo-600">{attempt.score}点</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">合格ライン</div>
                  <div className="text-3xl font-bold text-purple-600">{session.passingScore}点</div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">正解数</span>
                  <span className="font-semibold">
                    {correctAnswerCount} / {totalQuestionCount}
                  </span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">受験日時</span>
                  <span className="font-semibold">
                    {new Date(attempt.startedAt).toLocaleString("ja-JP")}
                  </span>
                </div>
                {attempt.completedAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">完了日時</span>
                    <span className="font-semibold">
                      {new Date(attempt.completedAt).toLocaleString("ja-JP")}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {attempt.passed && userBadge && (
            <Card className="border-yellow-300 bg-gradient-to-br from-yellow-50 to-orange-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-6 h-6 text-yellow-600" />
                  合格バッジ
                </CardTitle>
                <CardDescription>
                  おめでとうございます！以下のバッジを獲得しました。
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 bg-white p-4 rounded-lg">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                    <Award className="w-10 h-10 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-lg">{displayName} 合格</div>
                    <div className="text-sm text-gray-600">
                      発行日: {new Date(userBadge.issuedAt).toLocaleDateString("ja-JP")}
                    </div>
                  </div>
                  {badgeImageUrl && !isBadgeExpired && (
                    <Button variant="outline" size="sm" onClick={handleBadgeDownload}>
                      <Download className="w-4 h-4 mr-2" />
                      ダウンロード
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {attempt.passed && (
            <Card>
              <CardHeader>
                <CardTitle>合格後の登録フォーム</CardTitle>
                <CardDescription>合格者情報を入力し、確認メールを作成できます。</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="passed-name">お名前</Label>
                  <Input
                    id="passed-name"
                    value={passedForm.name}
                    onChange={(e) => setPassedForm({ ...passedForm, name: e.target.value })}
                    placeholder="木村謙介"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="passed-birth">生年月日（YYYY-MM-DD または YYYYMMDD）</Label>
                  <Input
                    id="passed-birth"
                    value={passedForm.birthDate}
                    onChange={(e) => setPassedForm({ ...passedForm, birthDate: e.target.value })}
                    placeholder="19850115"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="passed-gender">性別</Label>
                  <select
                    id="passed-gender"
                    value={passedForm.gender}
                    onChange={(e) => setPassedForm({ ...passedForm, gender: e.target.value })}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="male">男性</option>
                    <option value="female">女性</option>
                    <option value="unknown">回答しない</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="passed-prefecture">都道府県</Label>
                  <select
                    id="passed-prefecture"
                    value={passedForm.prefecture}
                    onChange={(e) => setPassedForm({ ...passedForm, prefecture: e.target.value })}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">都道府県を選択してください</option>
                    {prefectures.map((prefecture) => (
                      <option key={prefecture} value={prefecture}>{prefecture}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="passed-email">メールアドレス</Label>
                  <Input
                    id="passed-email"
                    type="email"
                    value={passedForm.email}
                    onChange={(e) => setPassedForm({ ...passedForm, email: e.target.value })}
                    placeholder="k-kimura@aim-llc.jp"
                  />
                </div>

                <div className="bg-muted rounded-lg p-4 space-y-1">
                  <div className="text-sm text-muted-foreground">送信先メールアドレス</div>
                  <div className="text-lg font-semibold break-all">{passedForm.email || "-"}</div>
                </div>

                <Button onClick={handleOpenConfirmMail} disabled={sendPassedInfoEmail.isPending}>
                  {sendPassedInfoEmail.isPending ? "送信中..." : "確認メールを送信する"}
                </Button>
              </CardContent>
            </Card>
          )}

          {answers && answers.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>回答詳細</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {answers.map((answer, index) => (
                    <div
                      key={answer.id}
                      className={`p-4 rounded-lg border ${
                        answer.isCorrect
                          ? "bg-green-50 border-green-200"
                          : "bg-red-50 border-red-200"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-lg">問題 {index + 1}</span>
                        {answer.isCorrect ? (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle2 className="w-5 h-5" />
                            <span className="text-sm font-medium">正解</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-red-600">
                            <XCircle className="w-5 h-5" />
                            <span className="text-sm font-medium">不正解</span>
                          </div>
                        )}
                      </div>
                      {answer.questionText && (
                        <p className="text-gray-800 mb-3 whitespace-pre-wrap">{answer.questionText}</p>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                        {["A", "B", "C", "D"].map((opt) => {
                          const optKey = `option${opt}` as keyof typeof answer;
                          const optText = answer[optKey];
                          if (!optText) return null;
                          const isSelected = answer.selectedAnswer === opt;
                          const isCorrectOpt = answer.correctAnswer === opt;
                          return (
                            <div
                              key={opt}
                              className={`px-3 py-2 rounded text-sm border ${
                                isCorrectOpt
                                  ? "bg-green-100 border-green-400 font-medium"
                                  : isSelected && !answer.isCorrect
                                    ? "bg-red-100 border-red-400 line-through"
                                    : "bg-white border-gray-200"
                              }`}
                            >
                              <span className="font-semibold mr-1">{opt}.</span>
                              {String(optText)}
                              {isCorrectOpt && <CheckCircle2 className="w-3 h-3 inline ml-1 text-green-600" />}
                            </div>
                          );
                        })}
                      </div>
                      {!answer.isCorrect && (
                        <div className="text-sm text-gray-600">
                          あなたの回答: <span className="font-medium text-red-600">{answer.selectedAnswer}</span>
                          {" → "}正解: <span className="font-medium text-green-600">{answer.correctAnswer}</span>
                        </div>
                      )}
                      {answer.explanation && (
                        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                          <span className="font-medium">解説: </span>{answer.explanation}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex gap-4">
            <Link href="/my-page" className="flex-1">
              <Button variant="outline" className="w-full">
                マイページに戻る
              </Button>
            </Link>
            <Link href="/" className="flex-1">
              <Button className="w-full">
                トップページに戻る
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
